import type { DesignerRegistrationForm } from '@/components/pages/userCenter/IdentityUpgrade/Designer/types'
import type {
  ServiceProviderFinancialForm,
  ServiceProviderRegistrationForm,
} from '@/components/pages/userCenter/IdentityUpgrade/ServiceProvider/types'
import type { IdentityApplicationInfo, IdentityApplicationStatus } from '@/types/designer'
import { httpClient } from '@/utils/request'

export type ServiceProviderRegistrationRequest = ServiceProviderRegistrationForm & ServiceProviderFinancialForm & {
  projects?: Array<{
    title: string
    cycle: string | [string, string]
    description: string
    images: string[]
  }>
}

/** 后端身份状态响应类型（对齐 IdentityStatusResponse schema） */
interface BackendIdentityStatusResponse {
  auth_status: string
  reject_reason: string | null
}

export const identityApi = {
  /**
   * 获取当前用户的身份认证状态
   * @param type 身份类型: 'designer' | 'provider'
   */
  getIdentityStatus(type: 'designer' | 'provider'): Promise<IdentityApplicationInfo> {
    return httpClient.get<BackendIdentityStatusResponse>(`/users/me/identity/${type}/status`).then((res) => {
      // 状态映射：后端 AuthStatus 到 前端 IdentityApplicationStatus
      const statusMap: Record<string, IdentityApplicationStatus> = {
        not_applied: 'not_applied',
        under_review: 'pending',
        certified: 'approved',
        rejected: 'rejected',
      }

      return {
        status: statusMap[res.auth_status] || 'not_applied',
        applicationId: undefined,
        submittedAt: undefined,
        reviewedAt: undefined,
        rejectReason: res.reject_reason || undefined,
        type,
      } as IdentityApplicationInfo
    })
  },

  /**
   * 获取设计师当前档案（用于重新编辑时回填）
   */
  getDesignerProfile() {
    return httpClient.get<any>('/users/me/identity/designer/profile')
  },

  /**
   * 获取设计师当前项目（用于重新编辑时回填）
   */
  getDesignerProjects() {
    return httpClient.get<any[]>('/users/me/identity/designer/projects')
  },

  /**
   * 获取服务商当前档案（用于重新编辑时回填）
   */
  getProviderProfile() {
    return httpClient.get<any>('/users/me/identity/provider/profile')
  },

  /**
   * 获取服务商当前项目（用于重新编辑时回填）
   */
  getProviderProjects() {
    return httpClient.get<any[]>('/users/me/identity/provider/projects')
  },

  /**
   * 提交设计师认证
   */
  submitDesignerRegistration(data: DesignerRegistrationForm) {
    return httpClient.post('/users/me/identity/designer', data)
  },

  /**
   * 提交服务商认证
   */
  submitServiceProviderRegistration(data: ServiceProviderRegistrationRequest) {
    return httpClient.post('/users/me/identity/provider', data)
  },

  /**
   * 重新提交设计师认证（驳回后使用）
   */
  resubmitDesignerRegistration(data: DesignerRegistrationForm) {
    return httpClient.post('/users/me/identity/designer/resubmit', data)
  },

  /**
   * 重新提交服务商认证（驳回后使用）
   */
  resubmitServiceProviderRegistration(data: ServiceProviderRegistrationRequest) {
    return httpClient.post('/users/me/identity/provider/resubmit', data)
  },
}
